import type { MetricsResponse } from '@chatbot/shared-types';
import { useCallback, useEffect, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AppShell } from '../components/AppShell.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { PageHeader } from '../components/PageHeader.tsx';
import { CONVERSATION_TABS, RouteTabs } from '../components/RouteTabs.tsx';
import { Skeleton } from '../components/Skeleton.tsx';
import { toast } from '../components/Toast.tsx';
import { Button, Card, CardBody, CardHeader, Input } from '../components/ui/index.ts';
import { useAsync } from '../hooks/useAsync.ts';
import { useTenantContext } from '../hooks/useTenantContext.ts';
import { api } from '../lib/api.ts';
import { conversationsApi } from '../lib/conversations-api.js';

function toDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function nDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toDate(d);
}

export function ConversationsMetricsPage() {
  const { activeTenantId, tenantIds } = useTenantContext();
  const [metrics, setMetrics] = useState<MetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [from, setFrom] = useState<string>(nDaysAgo(6)); // Default 7 days including today
  const [to, setTo] = useState<string>(toDate(new Date()));

  const { data: tenantsData } = useAsync(() => api.tenants.list());
  const tenants = tenantsData?.tenants ?? [];
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const tenantId = selectedTenant ?? activeTenantId ?? tenantIds[0] ?? '';
  const tenantName = tenants.find((t) => t.tenantId === tenantId)?.name;

  const fetchMetrics = useCallback(async () => {
    if (!tenantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Append UTC bounds for time ranges (start of fromDate UTC, end of toDate UTC)
      const fromISO = new Date(`${from}T00:00:00.000Z`).toISOString();
      const toISO = new Date(`${to}T23:59:59.999Z`).toISOString();

      const res = await conversationsApi.getMetrics(tenantId, fromISO, toISO);
      setMetrics(res);
    } catch (err) {
      setError((err as Error).message);
      toast.error(`Error al cargar métricas: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [tenantId, from, to]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const series = (metrics?.hourlySeries ?? []).map((h) => ({
    label: `${h.date.substring(5)} ${String(h.hour).padStart(2, '0')}h`,
    escalations: h.escalations,
    takeovers: h.takeovers,
    resolutions: h.resolutions,
    rtAvg: h.responseTimeSecCount > 0 ? h.responseTimeSecSum / h.responseTimeSecCount : null,
    aht: h.ahtSecCount > 0 ? h.ahtSecSum / h.ahtSecCount : null,
  }));

  return (
    <AppShell sectionTabs={<RouteTabs tabs={CONVERSATION_TABS} />}>
      <div className="flex flex-col h-full animate-fade-in bg-surface-sunken">
        <PageHeader
          title="Operadores — Live Chat"
          subtitle={tenantId ? `Tenant: ${tenantName ?? tenantId}` : 'Selecciona un tenant'}
        />

        <div className="flex-1 overflow-auto p-6 space-y-6">
          <Card variant="default">
            <CardBody className="flex gap-6 flex-wrap items-end justify-between">
              <div className="flex items-end gap-4">
                {tenants.length > 1 && (
                  <label className="flex flex-col gap-1">
                    <span className="text-caption font-medium text-content-secondary">Tenant</span>
                    <select
                      id="livechat-tenant-select"
                      data-testid="livechat-tenant-select"
                      value={tenantId}
                      onChange={(e) => setSelectedTenant(e.target.value)}
                      className="h-9 rounded-lg border border-edge bg-surface-base px-3 text-body-sm text-content-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {tenants.map((t) => (
                        <option key={t.tenantId} value={t.tenantId}>
                          {t.name || t.tenantId}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <Input
                  id="metrics-from"
                  label="Desde"
                  type="date"
                  size="sm"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
                <Input
                  id="metrics-to"
                  label="Hasta"
                  type="date"
                  size="sm"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
                <Button
                  id="metrics-refresh"
                  variant="outline"
                  size="sm"
                  onClick={fetchMetrics}
                  disabled={!tenantId}
                >
                  Actualizar
                </Button>
              </div>
            </CardBody>
          </Card>

          {loading && !metrics ? (
            <div className="space-y-6">
              <Skeleton rows={3} />
            </div>
          ) : error ? (
            <div className="p-6 text-center text-content-tertiary flex flex-col items-center justify-center gap-4 border border-edge-subtle rounded-lg bg-surface-base">
              <p className="text-danger-600 font-semibold">
                No se pudieron cargar las métricas: {error}
              </p>
              <button
                type="button"
                onClick={fetchMetrics}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded font-medium shadow-sm transition"
              >
                Reintentar
              </button>
            </div>
          ) : !metrics ? (
            <div className="p-12 text-center text-content-tertiary">
              <EmptyState message="Aún no hay métricas registradas para este período." />
            </div>
          ) : (
            <>
              <section
                id="metrics-kpis"
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4"
              >
                <KpiCard label="En cola ahora" value={metrics.queueDepth.toString()} />
                <KpiCard label="En atención" value={metrics.activeCount.toString()} />
                <KpiCard label="RT promedio" value={fmtSec(metrics.responseTimeAvgSec)} />
                <KpiCard label="AHT promedio" value={fmtSec(metrics.ahtAvgSec)} />
                <KpiCard label="Resolución %" value={fmtPct(metrics.resolutionRate)} />
              </section>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card variant="elevated">
                  <CardHeader>
                    <h3 className="text-sm font-medium text-content-secondary">Eventos por hora</h3>
                  </CardHeader>
                  <CardBody>
                    <div
                      className="h-72 w-full"
                      role="img"
                      aria-label="Gráfico de líneas que muestra la cantidad de eventos por hora (escaladas, tomadas y resueltas)"
                    >
                      {series.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-xs text-content-tertiary font-mono">
                          Sin datos en el rango seleccionado
                        </div>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            accessibilityLayer
                            data={series}
                            margin={{ top: 8, right: 16, left: -20, bottom: 8 }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              className="stroke-neutral-200 dark:stroke-neutral-800"
                            />
                            <XAxis
                              dataKey="label"
                              tick={{ fontSize: 9 }}
                              stroke="var(--scroll-thumb)"
                            />
                            <YAxis tick={{ fontSize: 9 }} stroke="var(--scroll-thumb)" />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: 'var(--color-surface-primary)',
                                borderColor: 'var(--color-border-default)',
                                fontSize: '11px',
                              }}
                            />
                            <Legend wrapperStyle={{ fontSize: '11px' }} />
                            <Line
                              type="monotone"
                              dataKey="escalations"
                              name="Escaladas"
                              stroke="var(--color-accent-secondary)"
                              dot={false}
                            />
                            <Line
                              type="monotone"
                              dataKey="takeovers"
                              name="Tomadas"
                              stroke="var(--color-accent-primary)"
                              dot={false}
                            />
                            <Line
                              type="monotone"
                              dataKey="resolutions"
                              name="Resueltas"
                              stroke="var(--color-state-success)"
                              dot={false}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </CardBody>
                </Card>

                <Card variant="elevated">
                  <CardHeader>
                    <h3 className="text-sm font-medium text-content-secondary">
                      Tiempos promedio por hora (segundos)
                    </h3>
                  </CardHeader>
                  <CardBody>
                    <div
                      className="h-72 w-full"
                      role="img"
                      aria-label="Gráfico de líneas que muestra el tiempo promedio de respuesta y el tiempo medio de gestión por hora en segundos"
                    >
                      {series.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-xs text-content-tertiary font-mono">
                          Sin datos en el rango seleccionado
                        </div>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            accessibilityLayer
                            data={series}
                            margin={{ top: 8, right: 16, left: -20, bottom: 8 }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              className="stroke-neutral-200 dark:stroke-neutral-800"
                            />
                            <XAxis
                              dataKey="label"
                              tick={{ fontSize: 9 }}
                              stroke="var(--scroll-thumb)"
                            />
                            <YAxis tick={{ fontSize: 9 }} stroke="var(--scroll-thumb)" />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: 'var(--color-surface-primary)',
                                borderColor: 'var(--color-border-default)',
                                fontSize: '11px',
                              }}
                            />
                            <Legend wrapperStyle={{ fontSize: '11px' }} />
                            <Line
                              type="monotone"
                              dataKey="rtAvg"
                              name="Response time"
                              stroke="var(--color-accent-secondary)"
                              dot={false}
                            />
                            <Line
                              type="monotone"
                              dataKey="aht"
                              name="AHT"
                              stroke="var(--color-accent-primary)"
                              dot={false}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </div>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <Card variant="default">
      <CardBody className="p-4">
        <div className="text-xs text-content-tertiary font-semibold uppercase tracking-wider">
          {label}
        </div>
        <div className="text-2xl font-bold text-content-primary mt-1">{value}</div>
      </CardBody>
    </Card>
  );
}

function fmtSec(n: number | null): string {
  if (n == null) return '—';
  if (n < 60) return `${n.toFixed(0)}s`;
  const m = Math.floor(n / 60);
  return `${m}m ${(n - m * 60).toFixed(0)}s`;
}

function fmtPct(n: number | null): string {
  if (n == null) return '—';
  return `${(n * 100).toFixed(1)}%`;
}
